/**
 * useVoiceInput — STT (speech-to-text).
 *
 * HLOS-323: prioriza AppleSpeechBridge (SFSpeechRecognizer nativo iOS)
 * que funciona de forma confiável. Em web/Android, usa Web Speech API.
 *
 * Fluxo:
 * 1. start("pt-BR") → pede autorização (se necessário) → inicia ditado
 * 2. Cada transcrição parcial é enviada para o draft
 * 3. Quando isFinal=true OU timeout, completa
 *
 * Constitucional:
 * - CL-08: opt-in; nunca inicia sem `voiceInputEnabled=true`.
 * - LGPD: áudio processado pelo reconhecedor do OS; nada sai do device
 *   sem consentimento explícito adicional.
 * - P-UX-01: zero escuta passiva; só ativa quando o aprendiz pede.
 * - P-UX-08: feedback honesto; timeout 8s se nada chegar.
 */

import { ref, onBeforeUnmount } from "vue";
import { appleSpeechBridge } from "../../capacitor/apple_speech_bridge";

type SpeechRecognitionEventLike = {
  results: ArrayLike<ArrayLike<{ transcript: string; isFinal?: boolean }>>;
  resultIndex: number;
};

type SpeechRecognitionErrorEventLike = { error: string; message?: string };

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
};

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface VoiceInputHandle {
  isListening: ReturnType<typeof ref<boolean>>;
  isSupported: boolean;
  error: ReturnType<typeof ref<string | null>>;
  start: (lang?: string) => void;
  stop: () => void;
  transcript: ReturnType<typeof ref<string>>;
}

export function useVoiceInput(onFinal?: (text: string) => void): VoiceInputHandle {
  const isSupported = getRecognitionCtor() !== null;
  const isListening = ref(false);
  const transcript = ref("");
  const error = ref<string | null>(null);
  let recognition: SpeechRecognitionLike | null = null;

  function build(lang: string) {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return null;
    const r = new Ctor();
    r.continuous = false;
    r.interimResults = true;
    r.lang = lang;
    r.onresult = (e: SpeechRecognitionEventLike) => {
      let text = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const alt = e.results[i]?.[0];
        if (alt) text += alt.transcript;
      }
      transcript.value = text;
      const last = e.results[e.results.length - 1];
      const lastAlt = last?.[0];
      if (lastAlt?.isFinal && onFinal) onFinal(text.trim());
    };
    r.onerror = (e: SpeechRecognitionErrorEventLike) => {
      error.value = e.error;
      isListening.value = false;
    };
    r.onend = () => {
      isListening.value = false;
    };
    return r;
  }

  // HLOS-323: usa Apple Speech nativo (SFSpeechRecognizer) no iOS.
  // No iOS, este é o caminho confiável; Web Speech API é fallback.
  const useNativeSTT = appleSpeechBridge.isAvailable();
  let nativeAuthRequested = false;
  // HLOS-374 inv-B: evita dispatch duplicado de onFinal. Apple envia chunk
  // final (isFinal=true) E depois estado "idle" — sem este flag, onFinal é
  // chamado duas vezes e o texto é concatenado duas vezes no draft.
  let finalDispatched = false;

  function start(lang: string = "pt-BR") {
    if (isListening.value) return;
    transcript.value = "";
    error.value = null;
    // HLOS-374 inv-B: reset flag para nova sessão.
    finalDispatched = false;

    // HLOS-323: caminho nativo iOS.
    if (useNativeSTT) {
      const runNative = async (granted: boolean) => {
        if (!granted) {
          error.value = "not-allowed";
          return;
        }
        appleSpeechBridge.start(lang, (result) => {
          if (result.state === "listening") {
            if (result.text !== undefined) {
              transcript.value = result.text;
              if (result.isFinal) {
                isListening.value = false;
                // HLOS-374 inv-B: dispatch onFinal apenas uma vez (chunk final
                // OU estado idle posterior — não ambos).
                if (!finalDispatched && onFinal && result.text.trim()) {
                  onFinal(result.text.trim());
                  finalDispatched = true;
                }
              }
            }
          } else if (result.state === "idle") {
            isListening.value = false;
            // HLOS-374 inv-B: se chunk final já disparou onFinal, não
            // disparar de novo no idle (evita duplicação no draft).
            if (!finalDispatched && transcript.value.trim() && onFinal) {
              onFinal(transcript.value.trim());
              finalDispatched = true;
            }
          } else if (result.state === "denied") {
            isListening.value = false;
            error.value = "not-allowed";
          } else if (result.state === "failed") {
            isListening.value = false;
            error.value = "recognition-failed";
          }
        });
        isListening.value = true;
      };
      if (nativeAuthRequested) {
        void runNative(true);
      } else {
        nativeAuthRequested = true;
        appleSpeechBridge.requestAuth().then(granted => {
          if (!granted) {
            error.value = "not-allowed";
            return;
          }
          void runNative(true);
        });
      }
      return;
    }

    // Fallback: Web Speech API (web/Android).
    if (!isSupported) {
      error.value = "unsupported";
      return;
    }
    recognition = build(lang);
    if (!recognition) return;
    // HLOS-319: timeout de "silêncio". Em iOS WKWebView, o Web Speech API
    // às vezes inicia mas nunca dispara o evento `result`. Após 8s sem
    // transcrição, paramos e marcamos como timeout para o usuário entender
    // que pode ser uma limitação do navegador.
    let receivedAnyResult = false;
    const origOnResult = recognition.onresult;
    recognition.onresult = (e: SpeechRecognitionEventLike) => {
      receivedAnyResult = true;
      origOnResult?.(e);
    };
    try {
      recognition.start();
      isListening.value = true;
      // Watchdog: se 8s se passarem sem nenhum resultado, paramos.
      setTimeout(() => {
        if (isListening.value && !receivedAnyResult) {
          try {
            recognition?.stop();
          } catch {
            // ignore
          }
          isListening.value = false;
          error.value = "no-results";
        }
      }, 8000);
    } catch (e) {
      error.value = e instanceof Error ? e.message : "start-failed";
      isListening.value = false;
    }
  }

  function stop() {
    if (useNativeSTT) {
      appleSpeechBridge.stop();
      isListening.value = false;
      return;
    }
    if (recognition && isListening.value) {
      try {
        recognition.stop();
      } catch {
        // ignore
      }
    }
    isListening.value = false;
  }

  onBeforeUnmount(() => {
    if (useNativeSTT) {
      appleSpeechBridge.stop();
      return;
    }
    if (recognition) {
      try {
        recognition.abort();
      } catch {
        // ignore
      }
    }
  });

  return { isSupported, isListening, error, start, stop, transcript };
}
